import { Button } from '@/components/ui/button';
import { LIMIT } from '@/constants';
import { Product, useLazyGetProductByBarcodeQuery } from '@/features/product';
import { SellerProductDetail, useGetSellerProductListQuery } from '@/features/seller';
import { useBarcodeScanner } from '@/hooks/useBarcode';
import { currencyFormatter, removeEmptyValues, decimal, DecimalCalc } from '@/utils';
import { createFileRoute, useLocation, useNavigate} from '@tanstack/react-router'
import { debounce } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { PaymentMethodEnum } from '@/enum';
import { Order } from '@/interfaces';
import "./Return.css"
import "../../create/Create.css"
import { appColors } from "@/constants/color";
import {
  IndianRupee,
  Percent,
  User2,
} from "lucide-react";
import { useGetPaymentMethodQuery } from '@/features/payment';
import { formDataOrderMapper, formDataReturnOrderMapper, useSellOrderMutation } from '@/features/order';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { currentUserSelector } from '@/features/auth';

const toggleIcon = "/icons/Toggle.png";
export const Route = createFileRoute('/(app)/(store)/sales/$orderId/return/')({
  component: RouteComponent,
})

enum DiscountType {
  Percent = "percent",
}

const initialValues = {
  customerDetails: "",
  paymentMethod: PaymentMethodEnum.Cash,
  productList: [],
  discount: { type: DiscountType.Percent, value: 0 },
};



function RouteComponent() {
  const location = useLocation();
  const order = location.state?.order as Order
  const { data: accounts = [] } = useGetPaymentMethodQuery({});

  const getDefaultPaymentMethod = () => {
    const savedMethod = localStorage.getItem("create-invoice-paymentMethod");
    if (savedMethod) {
      // If saved method is an account ID, convert to UPI enum
      if (accounts.some((acc) => acc._id === savedMethod)) {
        return PaymentMethodEnum.UPI;
      }
      return savedMethod;
    }
    return PaymentMethodEnum.Cash;
  };


  const methods = useForm({
    defaultValues: {
      ...initialValues,
      productList: [],
      returnedItems: [],
      returnedTotal: 0, 
      paymentMethod: getDefaultPaymentMethod(),
      buyerType: order?.buyerType,
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };
  
  const navigate = useNavigate();
  const [createOrder, { isLoading }] = useSellOrderMutation();

  const [isPaymentSummaryExpanded, setIsPaymentSummaryExpanded] =
    useState(true);
  const [isPaymentMethodExpanded, setIsPaymentMethodExpanded] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [orderData, setOrderData] = useState(null);

  const togglePaymentSummary = () => {
    setIsPaymentSummaryExpanded((prev) => !prev);
    setIsPaymentMethodExpanded(!isPaymentMethodExpanded);
  };

  const togglePaymentMethod = () => {
    setIsPaymentMethodExpanded((prev) => !prev);
    setIsPaymentSummaryExpanded(!isPaymentSummaryExpanded);
  };

  const onSubmit = async (data: any) => {
    try {
      const hasReturnItems =
        data.returnedItems && data.returnedItems.length > 0;

      let payload;
      if (hasReturnItems) {
        // For return orders, use a different mapper
        payload = formDataReturnOrderMapper(data, order._id);
      } else {
        // For regular orders
        payload = formDataOrderMapper(data);
      }
      const response = await createOrder(removeEmptyValues(payload)).unwrap();
      if (response.data) {
        toast.success(hasReturnItems ? "Return Processed Successfully." : "Order Created Successfully.")
        methods.reset(initialValues);
        setOrderData(response?.data);
        setShowPrintModal(true);
        window.localStorage.setItem(
          "create-invoice-paymentMethod",
          data?.paymentMethod
        );
      }
    } catch (error: any) {
      toast.error(error?.data.message ?? "Something went wrong.");
    }
  };

  const handlePrintConfirm = () => {
    setShowPrintModal(false);
    // Navigate to order page with print parameter
    navigate({
      to: "/order/$orderId",
      params: { orderId: orderData?._id },
      search: { print: true }, // Add print parameter
    });
  };

  const handlePrintSkip = () => {
    setShowPrintModal(false);
    // Navigate to order page without print
    navigate({
      to: "/order/$orderId",
      params: { orderId: orderData?._id },
      search: { print: false }, // Explicitly set print to false
    });
  };

  // Print Confirmation Modal Component
  const PrintConfirmModal = () => (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: appColors.modalOverlay,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: appColors.surfaceBase,
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '400px',
        width: '90%',
        textAlign: 'center',
        boxShadow: `0 10px 25px ${appColors.shadowOverlay}`
      }}>
        <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
          {orderData?.returnedItems ? "Return Processed Successfully!" : "Order Created Successfully!"}
        </h3>
        <p style={{ marginBottom: '24px', color: appColors.modalText, fontSize: '14px' }}>
          Would you like to print the {orderData?.returnedItems ? "return" : ""} bill now?
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Button 
            variant="outline" 
            onClick={handlePrintSkip}
            style={{ minWidth: '100px' }}
          >
            Skip
          </Button>
          <Button 
            onClick={handlePrintConfirm}
            style={{ minWidth: '100px' }}
          >
            Print Bill
          </Button>
        </div>
      </div>
    </div>
  );
  
  if (!order) return <div>Loading…</div>;
  return (
    <>
      <FormProvider {...methods}>
        <form className="generateBill"
          onKeyDown={handleKeyDown}
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <div className="billLeft">
            <CustomerSelector />
            <InvoiceItems />
            <ReturnOrder  order={order}/>
          </div>
          <div className="billRight">
            
            <PaymentSummary
              isExpanded={isPaymentSummaryExpanded}
              toggleExpanded={togglePaymentSummary}
            />
            <PaymentMethod
              isExpanded={isPaymentMethodExpanded}
              toggleExpanded={togglePaymentMethod}
              accounts={accounts}
            />
          </div>
            <div className="buttonRow generateBtn">
              <Button type="submit" size="xl"
              disabled={isLoading}
              >
                Generate Bill
              </Button>
            </div>
        </form>
      </FormProvider>
      {showPrintModal && <PrintConfirmModal />}
    </>
  );
}


function CustomerSelector() {
  const location = useLocation();
  const order = location.state?.order as Order;
  
  // Get customer details from order
  const customerName = order.buyerStore?.name || order.buyerUser?.name || "Anonymous";
  const phoneNumber = order.buyerStore?.phoneNumberList?.[0]?.phoneNumber || order.buyerUser?.phoneNumber || "";
  
  return (
    <div className="customerCard">
      <h3 className="containerHeading">Customer</h3>
      <div className="customerDetails">
        <div className='customerTags'>
          <User2 className="customerTypeIcon" />
          <div className="customerName">{customerName}</div>
        </div>
        {phoneNumber && (
          <div className="customerPhone">
            {phoneNumber.replace("(91)", "+91")}
          </div>
        )}
        {/* <div>
          <div className="customerType">{order.buyerType}</div>
        </div> */}
      </div>
    </div>
  );
}

function InvoiceItems() {
  const {
    watch,
    setValue,
    register,
    control,
    formState: { errors },
  } = useFormContext();
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [page] = useState(1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentUser = useSelector(currentUserSelector);
  const [showPurchasePrice, setShowPurchasePrice] = useState(false);
  const canViewPurchasePrice = currentUser?.profileDetail?.saleOrderConfig?.showPurchasePrice ?? true;
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const {
    data: productList,
    isLoading,
    error,
  } = useGetSellerProductListQuery({
    search: query,
    limit: LIMIT * 2,
    page,
  });
  const items = watch("productList") || [];

  // Handle clicks outside the dropdown
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  }, []);

  // Set up event listener for
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);
  
  const [getProductByBarcode] = useLazyGetProductByBarcodeQuery();

  useBarcodeScanner(async (barcode) => {
    const response = await getProductByBarcode({
      barcode: barcode.trim(),
    }).unwrap();
    if (response.data) {
      handleSelect({
        name: response.data.name,
        taxPercentageList: response.data.taxPercentageList,
        imageList: response.data.imageList,
        ...response.data?.sellerProductDetail?.at(0),
        quantity:
          response.data?.sellerProductDetail?.at(0)?.minimumQuantity
            ?.quantity ?? 1,
      });
    }
  });

  const updateQty = (index: number, delta: number) => {
    const updated = [...items];
    const currentQty = parseInt(updated[index].quantity) || 1;
    const initialQty = parseInt(updated[index].initialQuantity || 1);
    
    // Always use step of 1 for whole numbers only
    const step = 1;
    
    // Calculate the new quantity
    const newQty = currentQty + (delta * step);
    
    if (newQty < initialQty) {
      updated.splice(index, 1);
    } else {
      // Keep as integer
      updated[index].quantity = Math.max(initialQty, Math.floor(newQty));
    }
    setValue("productList", updated);
  };

  const removeItem = (index: number) => {
    const updated = [...items];
    updated.splice(index, 1);
    setValue("productList", updated);
  };

  const handlePriceChange = (
    index: number,
    field: "mrp" | "sellingPrice",
    value: number
  ) => {
    const updated = [...items];
    updated[index]["price"][field] = value;
    setValue("productList", updated);
  };

  const handleSelect = (product: any) => {
    const existingItemIndex = items.findIndex(
      (item: any) => item._id === product._id
    );
    const initialQuantity = product.quantity;
    if (existingItemIndex > -1) {
      const updatedItems = [...items];
      // Always increment by 1 (whole numbers only)
      updatedItems[existingItemIndex].quantity = 
        parseInt(updatedItems[existingItemIndex].quantity) + 1;
      setValue("productList", updatedItems);
    } else {
      setValue("productList", [...items, { ...product, initialQuantity }]);
    }
    setShowSuggestions(false);
    setQuery("");
    setActiveIndex(-1);
  };
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowSuggestions(false);
        setActiveIndex(-1);
      } else if (showSuggestions && productList && productList.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActiveIndex((prev) =>
            prev < productList.length - 1 ? prev + 1 : 0
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : productList.length - 1
          );
        } else if (e.key === "Enter" && activeIndex >= 0) {
          e.preventDefault();
          const selectedProduct = productList[activeIndex];
          handleSelect({
            name: selectedProduct.name,
            taxPercentageList: selectedProduct.taxPercentageList,
            imageList: selectedProduct.imageList,
            ...selectedProduct?.sellerProductDetail?.at(0),
            quantity:
              selectedProduct?.sellerProductDetail?.at(0)?.minimumQuantity
                ?.quantity ?? 1,
          });
          setQuery("");
        }
      }
    },
    [showSuggestions, productList, activeIndex]
  );


  const debouncedSetQuery = useCallback(
    debounce((val: string) => setQuery(val), 300),
    []
  );
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      debouncedSetQuery(event.target.value);
      if (!showSuggestions) {
        setShowSuggestions(true);
      }
      setActiveIndex(-1);
    },
    [showSuggestions, debouncedSetQuery]
  );

  const handleFocus = useCallback(() => {
    setQuery("");
    if (!showSuggestions) {
      setShowSuggestions(true);
    }
    setActiveIndex(-1);
  }, [showSuggestions]);

  const handleQuantityChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number, cb: Function) => {
      const initialQty = parseInt(items[index].minimumQuantity?.quantity || 1);
      const newValue = parseInt(event.target.value);
      
      if (isNaN(newValue) || newValue < initialQty) {
        cb(initialQty);
      } else {
        // Keep as integer
        cb(Math.floor(newValue));
      }
    },
    []
  );

  const renderItem = ({ item, index }: { item: any; index: number }) => {
    if (item?.sellerProductDetail?.length > 1) {
      return item.sellerProductDetail.map(
        (sellerProductDetail: SellerProductDetail, detailIndex: number) =>
          renderItem({
            item: {
              ...item,
              sellerProductDetail: [{ ...sellerProductDetail }],
            },
            index: index + detailIndex
          } as any)
      );
    }
    return (
      <div
        ref={el => itemsRef.current[index] = el!}
        className={`dropdownItem ${index === activeIndex ? 'active' : ''}`}
        onClick={() =>
          handleSelect({
            name: item.name,
            taxPercentageList: item.taxPercentageList,
            imageList: item.imageList,
            ...item?.sellerProductDetail?.at(0),
            quantity:
              item?.sellerProductDetail?.at(0)?.minimumQuantity?.quantity ?? 1,
          } as any)
        }
        onMouseEnter={() => setActiveIndex(index)}
      >
        <img
          src={item.imageList?.at(0)}
          alt={item.name}
          className="dropDownImage"
        />
        <div className="productDropDownContainer">
          <strong>{item.name}</strong>
          <div className="productDetails">
            {item?.sellerProductDetail?.at(0)?.price?.mrp?.toFixed(2) && (
              <span>
                MRP: ₹{item?.sellerProductDetail?.at(0)?.price?.mrp?.toFixed(2)}
              </span>
            )}

            {item?.sellerProductDetail
              ?.at(0)
              ?.price?.sellingPrice?.toFixed(2) && (
              <span>
                Selling: ₹
                {item?.sellerProductDetail
                  ?.at(0)
                  ?.price?.sellingPrice?.toFixed(2)}
              </span>
            )}

            {item?.sellerProductDetail?.at(0)?.barcode && (
              <span>barcode: {item?.sellerProductDetail?.at(0)?.barcode}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleToggle = useCallback(() => {
    setShowPurchasePrice((pre) => !pre);
  }, []);

  return (
    <div className="itemsCard">
      <div className="itemsHeader">
        <h3 className="containerHeading">Invoice Items</h3>
        <Button
          type="button"
          variant="link"
          to="/product/add-product"
          className="addItemsButton"
        >
          + Add Items
        </Button>
      </div>
      <div className="productDropdown" ref={dropdownRef} style={{ display: 'block' }}>
        <input
          type="text"
          placeholder="Search products..."
          style={{ width: canViewPurchasePrice ? 'calc(100% - 40px)' : '100%' }}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        />
        {canViewPurchasePrice && (
          <button
            className="purchasePrice"
            onClick={() => setShowPurchasePrice(!showPurchasePrice)}
            type="button"
            style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <p style={{ margin: 0, color: appColors.accentBlue, fontSize: '14px', fontWeight: 500 }}>Purchase Price</p>
            {showPurchasePrice ? (
              <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                <path d="M9.99967 7.5C10.6627 7.5 11.2986 7.76339 11.7674 8.23223C12.2363 8.70107 12.4997 9.33696 12.4997 10C12.4997 10.663 12.2363 11.2989 11.7674 11.7678C11.2986 12.2366 10.6627 12.5 9.99967 12.5C9.33663 12.5 8.70075 12.2366 8.23191 11.7678C7.76307 11.2989 7.49967 10.663 7.49967 10C7.49967 9.33696 7.76307 8.70107 8.23191 8.23223C8.70075 7.76339 9.33663 7.5 9.99967 7.5ZM9.99967 3.75C14.1663 3.75 17.7247 6.34167 19.1663 10C17.7247 13.6583 14.1663 16.25 9.99967 16.25C5.83301 16.25 2.27467 13.6583 0.833008 10C2.27467 6.34167 5.83301 3.75 9.99967 3.75ZM2.64967 10C3.32322 11.3753 4.3691 12.534 5.66841 13.3444C6.96772 14.1548 8.46834 14.5844 9.99967 14.5844C11.531 14.5844 13.0316 14.1548 14.3309 13.3444C15.6303 12.534 16.6761 11.3753 17.3497 10C16.6761 8.62474 15.6303 7.46604 14.3309 6.65562C13.0316 5.8452 11.531 5.41557 9.99967 5.41557C8.46834 5.41557 6.96772 5.8452 5.66841 6.65562C4.3691 7.46604 3.32322 8.62474 2.64967 10Z" fill={appColors.accentBlue} />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                <path d="M1.66634 4.39159L2.73301 3.33325L16.6663 17.2666L15.608 18.3333L13.0413 15.7666C12.083 16.0833 11.0663 16.2499 9.99967 16.2499C5.83301 16.2499 2.27467 13.6583 0.833008 9.99992C1.40801 8.53325 2.32467 7.24159 3.49134 6.21659L1.66634 4.39159ZM9.99967 7.49992C10.6627 7.49992 11.2986 7.76331 11.7674 8.23215C12.2363 8.70099 12.4997 9.33688 12.4997 9.99992C12.5001 10.2837 12.4522 10.5655 12.358 10.8333L9.16634 7.64159C9.43406 7.5474 9.71587 7.4995 9.99967 7.49992ZM9.99967 3.74992C14.1663 3.74992 17.7247 6.34159 19.1663 9.99992C18.4858 11.7274 17.3301 13.2268 15.833 14.3249L14.6497 13.1333C15.8021 12.3361 16.7316 11.2575 17.3497 9.99992C16.6761 8.62481 15.6301 7.46628 14.3308 6.65605C13.0315 5.84581 11.5309 5.41639 9.99967 5.41659C9.09134 5.41659 8.19968 5.56659 7.36634 5.83325L6.08301 4.55825C7.28301 4.04159 8.60801 3.74992 9.99967 3.74992ZM2.64967 9.99992C3.3233 11.375 4.36922 12.5336 5.66854 13.3438C6.96785 14.154 8.46843 14.5834 9.99967 14.5833C10.5747 14.5833 11.1413 14.5249 11.6663 14.4083L9.76634 12.4999C9.18648 12.4378 8.64536 12.179 8.23299 11.7666C7.82061 11.3542 7.56183 10.8131 7.49967 10.2333L4.66634 7.39158C3.84134 8.09992 3.14967 8.98325 2.64967 9.99992Z" fill={appColors.accentBlue} />
              </svg>
            )}
          </button>
        )}

        {showSuggestions && (
          <div className="dropdownCard">
            {isLoading ? (
              <div className="dropdownLoading">Searching products...</div>
            ) : error ? (
              <div className="dropdownError">Failed to load products</div>
            ) : productList && productList.length > 0 ? (
              productList.map((product,index) => renderItem({ item: product, index }))
            ) : query ? (
              <div className="dropdownEmpty">No products found</div>
            ) : (
              <div className="dropdownEmpty">
                Start typing to search products
              </div>
            )}
          </div>
        )}
      </div>
      {items.map(
        (
          item: Product &
            SellerProductDetail & { quantity: number; initialQuantity: number },
          i: number
        ) => {
          const isAtInitialQty =
            parseFloat(item.quantity) === parseFloat(item.minimumQuantity?.quantity || 1);

          return (
            <div className="invoiceItem" key={item._id}>
              <div className="itemDetails">
                <div className="itemImageContainer">
                  <img
                    src={
                      item?.imageList?.at(0) || "https://via.placeholder.com/50"
                    }
                    alt="item"
                    className="itemImage"
                  />
                </div>
                <div  className="itemInfo">
                  <p className="itemName">{item.name}</p>
                  <section>
                    <p className="itemPrice">
                      {item?.quantity} X{" "}
                      {currencyFormatter(item?.price?.sellingPrice)}
                    </p>
                    {showPurchasePrice && (
                      <p className="itemPrice">
                        Purchase Price{" "}
                        {currencyFormatter(item.price.purchasePrice)}
                      </p>
                    )}
                  </section>
                </div>
              </div>

              <div className="itemControls">
                <div className="quantityControls">
                  {isAtInitialQty ? (
                    <button
                      type="button"
                      className="quantityButton"
                      style={{ color: appColors.dangerRed }}
                      onClick={() => removeItem(i)}
                      title="Remove item"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="dustbinIcon"
                      >
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="quantityButton"
                      onClick={() => updateQty(i, -1)}
                    >
                      -
                    </button>
                  )}
                  <Controller
                    control={control}
                    name={`productList.${i}.quantity`}
                    render={({
                      field: { onBlur, onChange, value },
                      fieldState: { error },
                    }) => (
                      <input
                        type="number"
                        step="1"
                        className={`quantityInput ${error ? "quantityInputError" : ""}`}
                        value={value}
                        onBlur={onBlur}
                        onChange={(event) =>
                          handleQuantityChange(event, i, onChange)
                        }
                        onWheel={(e) => e.currentTarget.blur()} 
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="quantityButton"
                    onClick={() => updateQty(i, 1)}
                  >
                    +
                  </button>
                </div>
                <div className="priceDisplay">
                  <p className="priceLabel">MRP</p>
                  <input
                    type="number"
                    className="priceInput"
                    value={item?.price?.mrp}
                    onChange={(e) =>
                      handlePriceChange(i, "mrp", Number(e.target.value))
                    }
                    min="0"
                    onWheel={(e) => e.currentTarget.blur()} 
                    disabled
                  />
                </div>
                <div className="validationContainer">
                  <div className="priceDisplay">
                    <p className="priceLabel">Selling</p>
                    <input
                      {...register(`productList.${i}.price.sellingPrice`, {
                        validate: (value) =>
                          Number(value) <= Number(item?.price?.mrp) ||
                          "Selling Price must be less then MRP",
                      })}
                      type="number"
                      className="priceInput"
                      defaultValue={item?.price?.mrp}
                      value={item?.price.sellingPrice}
                      onWheel={(e) => e.currentTarget.blur()} 
                    />
                  </div>
                  {errors.productList?.[i]?.price?.sellingPrice && (
                    <p className="errorMessage">
                      {errors.productList[i].price.sellingPrice.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        }
      )}
    </div>
  );
}

const PaymentMethod = ({ isExpanded, toggleExpanded, accounts }) => {
  const { register, watch, setValue } = useFormContext();
  const [isSplitActive, setIsSplitActive] = useState(false);
  const [defaultMethodSet, setDefaultMethodSet] = useState(false);
  const selectedPaymentMethods = watch("paymentMethods") || [];
  const selectedMethod = watch("paymentMethod");
  const splitAmounts = watch("splitAmounts") || {};

  useEffect(() => {
    if (accounts.length > 0 && !defaultMethodSet) {
      const savedMethod = localStorage.getItem("create-invoice-paymentMethod");

      if (savedMethod) {
        const isValidAccount = accounts.some((acc) => acc._id === savedMethod);
        const isValidEnum = Object.values(PaymentMethodEnum).includes(
          savedMethod as PaymentMethodEnum
        );

        if (isValidAccount || isValidEnum) {
          setValue("paymentMethod", savedMethod);
          setValue("paymentMethods", [savedMethod]);
        } else {
          const firstUpiAccount = accounts.find((acc) => acc.upi?.upiId);
          const defaultMethod = firstUpiAccount
            ? firstUpiAccount._id
            : PaymentMethodEnum.Cash;
          setValue("paymentMethod", defaultMethod);
          setValue("paymentMethods", [defaultMethod]);
          localStorage.setItem("create-invoice-paymentMethod", defaultMethod);
        }
      } else {
        const firstUpiAccount = accounts.find((acc) => acc.upi?.upiId);
        const defaultMethod = firstUpiAccount
          ? firstUpiAccount._id
          : PaymentMethodEnum.Cash;
        setValue("paymentMethod", defaultMethod);
        setValue("paymentMethods", [defaultMethod]);
        localStorage.setItem("create-invoice-paymentMethod", defaultMethod);
      }

      setDefaultMethodSet(true);
    }
  }, [accounts, defaultMethodSet, setValue]);

  // Automatically select/deselect methods based on split amount input
  useEffect(() => {
    if (isSplitActive) {
      const currentMethods = selectedPaymentMethods;
      const updatedMethods = [];
      
      // Check all payment methods
      const allMethods = [
        ...accounts.map(acc => acc._id),
        ...Object.values(PaymentMethodEnum)
      ];
      
      allMethods.forEach(methodId => {
        const amount = parseFloat(splitAmounts[methodId] || 0);
        
        if (amount > 0) {
          // Add to selection if amount > 0
          updatedMethods.push(methodId);
        }
      });
      
      // Update payment methods if changed
      if (JSON.stringify(updatedMethods.sort()) !== JSON.stringify(currentMethods.sort())) {
        setValue("paymentMethods", updatedMethods);
      }
    }
  }, [splitAmounts, isSplitActive, setValue]);

  const activateSplitPayment = () => {
    setIsSplitActive(true);
    // Initialize split amounts when activating split payment
    const initialSplitAmounts = {};
    accounts.forEach((account) => {
      initialSplitAmounts[account._id] = "";
    });
    Object.values(PaymentMethodEnum).forEach((method) => {
      initialSplitAmounts[method] = "";
    });
    setValue("splitAmounts", initialSplitAmounts);
    setValue("paymentMethods", []);
  };

  const deactivateSplitPayment = () => {
    setIsSplitActive(false);
    // Clear split amounts when deactivating split payment
    setValue("splitAmounts", {});
    // Reset to single selection mode
    if (selectedPaymentMethods.length > 0) {
      setValue("paymentMethod", selectedPaymentMethods[0]);
    }
  };

  const handleMethodSelection = (methodId) => {
    if (!isSplitActive) {
      const methodToStore = accounts.find((acc) => acc._id === methodId)
        ? methodId
        : methodId;
      // Single selection mode - update both paymentMethod and paymentMethods
      setValue("paymentMethod", methodId);
      setValue("paymentMethods", [methodId]);
      localStorage.setItem("create-invoice-paymentMethod", methodToStore);
    }
    // In split mode, selection is handled automatically by amount input
  };

  const getDisplayMethod = () => {
    if (isSplitActive) {
      if (selectedPaymentMethods.length === 0) return "";

      const methodNames = selectedPaymentMethods.map((methodId) => {
        const account = accounts.find((acc) => acc._id === methodId);
        if (account) {
          return `${account.name} (${account.upi?.upiId || "UPI"})`;
        }

        switch (methodId) {
          case PaymentMethodEnum.Cash:
            return "Cash";
          case PaymentMethodEnum.Card:
            return "Card";
          case PaymentMethodEnum.Other:
            return "Other";
          default:
            return "";
        }
      });

      return methodNames.join(", ");
    } else {
      if (selectedMethod === PaymentMethodEnum.Cash) return "Cash";
      if (selectedMethod === PaymentMethodEnum.Card) return "Card";
      if (selectedMethod === PaymentMethodEnum.Other) return "Other";

      const selectedAccount = accounts.find(
        (acc) => acc._id === selectedMethod
      );
      return selectedAccount
        ? selectedAccount.upi?.upiId || "UPI"
        : "";
    }
  };

  const isMethodSelected = (methodId) => {
    if (!isSplitActive) {
      return selectedMethod === methodId;
    }
    return (watch("paymentMethods") || []).includes(methodId);
  };

  return (
    <div
      className={`paymentMethodContainer ${isExpanded ? "expanded" : "collapsed"}`}
    >
      <div className="paymentMethodHeader" onClick={toggleExpanded}>
        <h3 className="containerHeading">Payment Method</h3>
        <div className="paymentMethodSummary">
          {getDisplayMethod()} 
        </div>
      </div>
      {isExpanded && (
        <>
          <div className="splitContainer">
            <button
              className={`splitToggle ${!isSplitActive ? "active" : ""}`}
              onClick={deactivateSplitPayment}
              type="button"
            >
              Payment
            </button>
            <button
              type="button"
              className={`splitToggle ${isSplitActive ? "active" : ""}`}
              onClick={activateSplitPayment}
            >
              <span>Split</span>
            </button>
          </div>
          <h4 className="paymentSubheading">UPI</h4>
          <div className="paymentMethodCard">
            {accounts?.map((bank) => (
              <div
                key={bank._id}
                className={`bankOption ${isMethodSelected(bank._id) ? "selected" : ""}`}
              >
                <label className="bankLabel">
                  <div className="bankInfo">
                    <img
                      src={bank.logoURL}
                      alt={bank.name}
                      className="bankLogo"
                    />
                    <div className="bankText">
                      <span className="bankName">{bank.name}</span>
                      <span className="bankDetails">{bank.upi?.upiId}</span>
                    </div>
                  </div>
                  <div className="paymentRightSection">
                    {isSplitActive && (
                      <div className="splitAmountInput">
                        <span className="currencySymbol">₹</span>
                        <input
                          type="number"
                          className="amountInput"
                          {...register(`splitAmounts.${bank._id}`)}
                          placeholder="0.00"
                          step={0.1}
                          onWheel={(e) => e.currentTarget.blur()} 
                        />
                      </div>
                    )}
                    {!isSplitActive && (
                      <input
                        type="radio"
                        checked={isMethodSelected(bank._id)}
                        onChange={() => handleMethodSelection(bank._id)}
                        className="bankRadioInput"
                      />
                    )}
                  </div>
                </label>
              </div>
            ))}
          </div>

          {/* Cash Option */}
          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Cash) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Cash</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Cash}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()} 
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Cash)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Cash)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
          
          {/* Card Option */}
          <div className='breakLine'/>    
          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Card) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Debit / Credit / ATM Card</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Card}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()} 
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Card)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Card)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
          
          {/* Other Option */}
          <div className='breakLine'/>    
          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Other) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Other</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Other}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()} 
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Other)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Other)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
        </>
      )}
    </div>
  );
};

function PaymentSummary({ isExpanded, toggleExpanded }) {
  const { watch, setValue, control } = useFormContext();
  const [isApplied, setIsApplied] = useState(false);
  const [isToggle, setIsToggle] = useState(false);
  const currentUser = useSelector(currentUserSelector);
  const [showPurchasePriceTotal, setShowPurchasePriceTotal] = useState(false);
  const canViewPurchasePrice = currentUser?.profileDetail?.saleOrderConfig?.showPurchasePrice ?? true;
  const [isEditing, setIsEditing] = useState(false);
  const [isSummaryVisible, setIsSummaryVisible] = useState(true);

  const items = watch("productList") || [];
  const discount = watch("discount") || {
    type: DiscountType.Percent,
    value: 0,
  };

  const returnedTotal = watch("returnedTotal") || 0;

  // helper to avoid floating precision issues
  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  const grossPrice = round2(
    DecimalCalc.sum(
      items.map((item: SellerProductDetail & { quantity: number }) =>
        decimal(Math.max(item?.price?.mrp, item.price.sellingPrice)).mul(item.quantity)
      )
    ).toNumber()
  );
  const itemTotal = (
    DecimalCalc.sum(
      items.map((item: SellerProductDetail & { quantity: number }) =>
        decimal(item?.price?.sellingPrice).mul(item.quantity)
      )
    ).toNumber()
  );
  const productDiscount = (
    DecimalCalc.sum(
      items.map((item: SellerProductDetail & { quantity: number }) =>
        decimal(
          item.price.mrp > Number(item?.price?.sellingPrice)
            ? item?.price?.mrp - item?.price?.sellingPrice
            : 0
        ).mul(item.quantity)
      )
    ).toNumber()
  );
  const additionalDiscount = 
    isApplied
      ? discount.type === "amount"
        ? discount.value
        : decimal(itemTotal).mul(discount.value).div(100).toNumber()
      : 0

  const adjustedItemTotal = (itemTotal - returnedTotal);

  const computedTotalAmountToReceive = Math.max(-returnedTotal, adjustedItemTotal - additionalDiscount)

  const [totalAmountStr, setTotalAmountStr] = useState<string>(
    computedTotalAmountToReceive.toString()
  );

  const purchaseTotal = DecimalCalc.sum(
    items.map((item: SellerProductDetail & { quantity: number }) =>
      decimal(item?.price?.purchasePrice || 0).mul(item?.quantity || 0)
    )
  ).toNumber();

  useEffect(() => {
    setTotalAmountStr(computedTotalAmountToReceive.toString());
  }, [computedTotalAmountToReceive]);

  const handleQuickDiscount = (percentage: number) => {
    setValue("discount", { type: DiscountType.Percent, value: percentage });
    setIsApplied(true);
    setIsToggle(false);
  };

  const quickDiscounts = [10, 30, 50];

  const handleFocus = () => {
    setIsEditing(true);
    setTotalAmountStr(computedTotalAmountToReceive.toString());
  };

  const handleChange = (raw: string) => {
    setTotalAmountStr(raw); // allow empty string for typing
  };

  const handleBlur = () => {
    const parsed = totalAmountStr === "" ? 0 : Number(totalAmountStr)
    const safeParsed = Number.isFinite(parsed) ? parsed : 0

    const currentReturned = returnedTotal || 0
    const currentDiscountType = discount?.type
    const newAmount = round2(safeParsed)

    // helper to compute rupee discount from current discount watch values
    const currentAdditionalDiscount = isApplied ? (discount.type === "amount" ? discount.value || 0 : (itemTotal * (discount.value || 0)) / 100) : 0
    

    // Calculate the base amount without any additional discount
    const baseAmount = round2(itemTotal - currentReturned)

    // If user wants a different total, calculate what needs to change
    const difference = round2(newAmount - baseAmount)


    if (difference === 0) {
      // No change needed - reset discount
      setIsApplied(false)
      setValue("discount", { type: discount.type, value: 0 })
      setIsEditing(false)
      setTotalAmountStr(baseAmount.toFixed(2))
      return
    }

    if (difference > 0) {
      // User wants higher total - reduce returned items first
      if (currentReturned > 0) {
        const reduceReturned = Math.min(currentReturned, difference)
        const newReturnedTotal = (currentReturned - reduceReturned)
        setValue("returnedTotal", newReturnedTotal)

        // If returned total becomes 0, clear returned items
        if (newReturnedTotal <= 0) {
          setValue("returnedItems", []);
        }

        const finalAmount = (itemTotal - newReturnedTotal - currentAdditionalDiscount)
        setIsEditing(false)
        setTotalAmountStr(finalAmount.toFixed(2))
        return
      }
    } else {

      
      // User wants lower total (more negative) - apply discount first, then adjust returned items
      const neededReduction = Math.abs(difference)
      const maxDiscountAllowed = Math.max(0, (grossPrice - itemTotal)) // available margin

      // Prioritize discount up to the purchased item amount (itemTotal)
      const maxDiscountForPurchased = itemTotal
      const effectiveMaxDiscount = Math.max(maxDiscountAllowed, maxDiscountForPurchased)

      if (newAmount < 0) {
          setValue("productList", []);
      }

      if (neededReduction <= effectiveMaxDiscount) {
        // Can be fully covered by additional discount
        setIsApplied(true)

        if (currentDiscountType === DiscountType.Percent) {
          const percent = itemTotal ? ((neededReduction / itemTotal) * 100) : 0
          setValue("discount", { type: DiscountType.Percent, value: Math.min(percent, 100) })
        } else {
          setValue("discount", { type: "amount", value: neededReduction })
        }

        const finalAmount = (itemTotal - currentReturned - neededReduction)
        setIsEditing(false)
        setTotalAmountStr(finalAmount.toString())
        return
      } else {
        // Need both discount and returned items adjustment
        // Apply maximum discount first
        setIsApplied(true)

        if (currentDiscountType === DiscountType.Percent) {
          const percentForMax = itemTotal ? ((effectiveMaxDiscount / itemTotal) * 100) : 0
          setValue("discount", { type: DiscountType.Percent, value: Math.min(percentForMax, 100) })
        } else {
          setValue("discount", { type: "amount", value: effectiveMaxDiscount })
        }

        // Apply remaining reduction to returned items
        const remainingReduction = (neededReduction - effectiveMaxDiscount)
        const newReturnedTotal = (currentReturned + remainingReduction)
        setValue("returnedTotal", newReturnedTotal)

        const finalAmount = (itemTotal - newReturnedTotal - effectiveMaxDiscount)
        setIsEditing(false)
        setTotalAmountStr(finalAmount.toString())
        return
      }
      
    }
    
  }

  function handleDiscountChange(
    raw: string,
    discountType: DiscountType,
    onChange: (val: number | "") => void
  ) {
    if (raw === "") {
      // allow empty input for typing
      onChange("");
      return;
    }

    let val = Number(raw);

    if (val < 0) {
      val = 0;
    }

    if (discountType === DiscountType.Percent) {
      onChange(val > 100 ? 100 : val);
    } else {
      onChange(val);
    }
  }

  return (
    <div
      className={`paymentSummaryCard ${isExpanded ? "expanded" : "collapsed"}`}
    >
      <div className="paymentSummaryHeader" onClick={toggleExpanded}>
        <h3 className="containerHeading">Payment Summary</h3>
        <div className="paymentSummaryTotal">
          {currencyFormatter(computedTotalAmountToReceive)}
        </div>
      </div>

      {isExpanded && (
        <>
        <h3 className="f500 pLeft4 pbottom4 ptop4" >Discount</h3>
          <div className="discountContainer">
            <Controller
              control={control}
              name="discount.value"
              render={({ field }) => (
                <input
                  type="number"
                  className="discountInput"
                  step="any"
                  min={0}
                  max={discount.type === DiscountType.Percent ? 100 : undefined}
                  value={field.value ?? ""}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) =>
                    handleDiscountChange(
                      e.target.value,
                      discount.type,
                      field.onChange
                    )
                  }
                />
              )}
            />

            {/* toggle icon start*/}
            <div className="togglePercentageContainer">
              {/* % Button */}
              <button
                type="button"
                onClick={() => {
                  if (discount.type === "amount") {
                    const percentValue =
                      itemTotal > 0
                        ? Math.min((discount.value / itemTotal) * 100, 100)
                        : 0;
                    setValue("discount", {
                      type: DiscountType.Percent,
                      value: percentValue,
                    });
                  }
                  setIsToggle(false); // ✅ update toggle
                }}
                className={`togglePercentageBtn ${!isToggle ? "Switched" : ""}`}
              >
                <Percent size={20} />
              </button>

              {/* ₹ Button */}
              <button
                type="button"
                onClick={() => {
                  if (discount.type === DiscountType.Percent) {
                    const rupeeValue = (itemTotal * discount.value) / 100;
                    setValue("discount", { type: "amount", value: rupeeValue });
                  }
                  setIsToggle(true); // ✅ update toggle
                }}
                className={`togglePercentageBtn ${isToggle ? "Switched" : ""}`}
              >
                <IndianRupee size={20} />
              </button>
            </div>
            <button
              className={`applyDiscountButton ${isApplied ? "discountApplied" : ""}`}
              onClick={() => {
                const newApplied = !isApplied;
                setIsApplied(newApplied);

                if (!newApplied) {
                  setValue("discount", { type: discount.type, value: 0 }); // reset when removed
                } else {
                  setValue("discount", {
                    type: discount.type,
                    value: discount.value,
                  }); // ✅ ensure form has correct discount
                }
              }}
              type="button"
            >
              {isApplied ? "Applied" : "Apply"}
            </button>
          </div>

          <div className="quickDiscounts">
            {quickDiscounts.map((percentage) => (
              <button
                key={percentage}
                className={`quickDiscountButton ${
                  discount.type === DiscountType.Percent &&
                  discount.value === percentage
                    ? "quickDiscountSelected"
                    : ""
                }`}
                onClick={() => handleQuickDiscount(percentage)}
                type="button"
              >
                {percentage}%
              </button>
            ))}
          </div>
           <div className="breakLine2"/>

          <div className="paymentSummary">
            <div className="summaryHeader">
              <span>Total Bill</span>
              <button
                className={`summaryToggleButton ${!isSummaryVisible ? "summaryToggleRotated" : ""}`}
                onClick={() => setIsSummaryVisible(!isSummaryVisible)}
                type="button"
              >
                <span>Details</span>
                <img
                  src={toggleIcon}
                  alt="Toggle details"
                  className={`toggleIcon ${!isSummaryVisible ? "toggleIconRotated" : ""}`}
                />
              </button>
            </div>

            {isSummaryVisible && (
              <div className="summaryDetails">
                <div className="summaryRow">
                  <span>Gross Price</span>
                  <span>
                    {currencyFormatter(Math.max(grossPrice, itemTotal))}
                  </span>
                </div>

                <div className="summaryRow">
                  <span>Product Discount</span>
                  <span>-{currencyFormatter(productDiscount)}</span>
                </div>
                <div className="summaryRow">
                  <span>Item Total</span>
                  <span>{currencyFormatter(itemTotal)}</span>
                </div>
                {returnedTotal > 0 && (
                  <div className="summaryRow">
                    <span>Returned Items</span>
                    <span style={{ color: appColors.dangerRed }}>
                      -{currencyFormatter(returnedTotal)}
                    </span>
                  </div>
                )}
                <div className="summaryRow">
                  <span>Additional Discount</span>
                  <span>-{currencyFormatter(additionalDiscount)}</span>
                </div>
              </div>
            )}

            <div className="summaryTotal">
              <div className="totalPurchasePrice">
                <span>Total Amount to receive</span>
                {canViewPurchasePrice && (
                  <button
                    className="purchasePrice"
                    onClick={() => setShowPurchasePriceTotal(!showPurchasePriceTotal)}
                    type="button"
                  >
                    {showPurchasePriceTotal ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                        <path d="M9.99967 7.5C10.6627 7.5 11.2986 7.76339 11.7674 8.23223C12.2363 8.70107 12.4997 9.33696 12.4997 10C12.4997 10.663 12.2363 11.2989 11.7674 11.7678C11.2986 12.2366 10.6627 12.5 9.99967 12.5C9.33663 12.5 8.70075 12.2366 8.23191 11.7678C7.76307 11.2989 7.49967 10.663 7.49967 10C7.49967 9.33696 7.76307 8.70107 8.23191 8.23223C8.70075 7.76339 9.33663 7.5 9.99967 7.5ZM9.99967 3.75C14.1663 3.75 17.7247 6.34167 19.1663 10C17.7247 13.6583 14.1663 16.25 9.99967 16.25C5.83301 16.25 2.27467 13.6583 0.833008 10C2.27467 6.34167 5.83301 3.75 9.99967 3.75ZM2.64967 10C3.32322 11.3753 4.3691 12.534 5.66841 13.3444C6.96772 14.1548 8.46834 14.5844 9.99967 14.5844C11.531 14.5844 13.0316 14.1548 14.3309 13.3444C15.6303 12.534 16.6761 11.3753 17.3497 10C16.6761 8.62474 15.6303 7.46604 14.3309 6.65562C13.0316 5.8452 11.531 5.41557 9.99967 5.41557C8.46834 5.41557 6.96772 5.8452 5.66841 6.65562C4.3691 7.46604 3.32322 8.62474 2.64967 10Z" fill={appColors.accentBlue} />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                        <path d="M1.66634 4.39159L2.73301 3.33325L16.6663 17.2666L15.608 18.3333L13.0413 15.7666C12.083 16.0833 11.0663 16.2499 9.99967 16.2499C5.83301 16.2499 2.27467 13.6583 0.833008 9.99992C1.40801 8.53325 2.32467 7.24159 3.49134 6.21659L1.66634 4.39159ZM9.99967 7.49992C10.6627 7.49992 11.2986 7.76331 11.7674 8.23215C12.2363 8.70099 12.4997 9.33688 12.4997 9.99992C12.5001 10.2837 12.4522 10.5655 12.358 10.8333L9.16634 7.64159C9.43406 7.5474 9.71587 7.4995 9.99967 7.49992ZM9.99967 3.74992C14.1663 3.74992 17.7247 6.34159 19.1663 9.99992C18.4858 11.7274 17.3301 13.2268 15.833 14.3249L14.6497 13.1333C15.8021 12.3361 16.7316 11.2575 17.3497 9.99992C16.6761 8.62481 15.6301 7.46628 14.3308 6.65605C13.0315 5.84581 11.5309 5.41639 9.99967 5.41659C9.09134 5.41659 8.19968 5.56659 7.36634 5.83325L6.08301 4.55825C7.28301 4.04159 8.60801 3.74992 9.99967 3.74992ZM2.64967 9.99992C3.3233 11.375 4.36922 12.5336 5.66854 13.3438C6.96785 14.154 8.46843 14.5834 9.99967 14.5833C10.5747 14.5833 11.1413 14.5249 11.6663 14.4083L9.76634 12.4999C9.18648 12.4378 8.64536 12.179 8.23299 11.7666C7.82061 11.3542 7.56183 10.8131 7.49967 10.2333L4.66634 7.39158C3.84134 8.09992 3.14967 8.98325 2.64967 9.99992Z" fill={appColors.accentBlue} />
                      </svg>
                    )}
                    {showPurchasePriceTotal ? (
                      <p>{currencyFormatter(purchaseTotal)}</p>
                    ) : (
                      <p>Purchase Price Total</p>
                    )}
                  </button>
                )}
              </div>

              <input
                type="number"
                className="totalAmountInput"
                value={
                  isEditing
                    ? totalAmountStr
                    : computedTotalAmountToReceive
                }
                onFocus={handleFocus}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function ReturnOrder({ order }: { order: Order }) {
  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();

  const returnedItems = watch("returnedItems") || [];
  
  const handleReturnItem = (index: number) => {
    const item = orderItemsForReturn[index];
    const existingReturnIndex = returnedItems.findIndex(
      (returnItem) => returnItem._id === item._id
    );

    if (existingReturnIndex === -1) {
      // Add item to returned items with initial quantity 1
      const newReturnedItems = [
        ...returnedItems,
        {
          ...item,
          returnQuantity: 1,
          initialQuantity: item.quantity,
        },
      ];
      setValue("returnedItems", newReturnedItems);
      calculateReturnTotal(newReturnedItems);
    }
  };

  const updateReturnQty = (index: number, delta: number) => {
    const updated = [...returnedItems];
    const currentQty = updated[index].returnQuantity;
    const initialQty = updated[index].initialQuantity;

    if (currentQty + delta <= 0) {
      // Remove item if quantity becomes 0
      updated.splice(index, 1);
    } else if (currentQty + delta <= initialQty) {
      // Only allow decreasing quantity up to initial quantity
      updated[index].returnQuantity = currentQty + delta;
    }
    setValue("returnedItems", updated);
    calculateReturnTotal(updated);
  };

  const calculateReturnTotal = (items: any[]) => {
    const total = DecimalCalc.sum(
      items.map(item =>
        decimal(item.price.sellingPrice).mul(item.returnQuantity)
      )
    ).toNumber();
    setValue("returnedTotal", total);
  };

  const orderItemsForReturn = order.itemList?.map((item) => ({
    
    _id: item.product._id,
    sellerProductDetailId: item.product?.sellerProductDetailId ?? item.sellerProductDetail?._id,
    name: item.product.name,
    imageList: item.product.image ?? [],
    taxPercentageList: item.totalTaxPercentagePrice ?? [],
    quantity: item.unitWiseQuantity?.quantity ?? 1,
    initialQuantity: item.unitWiseQuantity?.quantity ?? 1,
    productUnit: item.unitWiseQuantity?.productUnit,
    price: {
      mrp: item.grossPrice ?? 0,
      sellingPrice: item.pricePerUnit.totalAmount ?? 0,
      purchasePrice: item.totalPrice ?? 0,
    },
  })) || [];

  return (
    <div className="itemsCard">
      <div className='returnHeader'>
        <h3 className="containerHeading">Return Items</h3>
      </div>

      {orderItemsForReturn.map((item: any, i: number) => {
        const returnItem = returnedItems.find((ri) => ri._id === item._id);
        const isReturning = !!returnItem;

        return (
          <div className="invoiceItem" key={item._id}>
            <div className="itemDetails">
              <div className="itemImageContainer">
                <img
                  src={item?.imageList || "https://via.placeholder.com/50"}
                  alt="item"
                  className="itemImage"
                />
              </div>
              <div>
                <p className="itemName">{item.name}</p>
                <section>
                  <p className="itemPrice">
                    {item.quantity} X{" "}
                    {currencyFormatter(item.price.sellingPrice)}
                  </p>
                </section>
              </div>
            </div>

            <div className="itemControls">
              {!isReturning ? (
                <button
                  type="button"
                  className="returnButton"
                  onClick={() => handleReturnItem(i)}
                >
                  Return
                </button>
              ) : (
                <div className="quantityControls">
                  <Button
                    variant="ghost"
                    type="button"
                    className="quantityButton"
                    onClick={() =>
                      updateReturnQty(
                        returnedItems.findIndex((ri) => ri._id === item._id),
                        -1
                      )
                    }
                  >
                    -
                  </Button>
                  {/* need to make this span as iput anf from here also qty can be change */}
                  <span className="quantityValue">
                    {returnItem.returnQuantity}
                  </span>
                  <Button
                    variant="ghost"
                    type="button"
                    className="quantityButton"
                    onClick={() =>
                      updateReturnQty(
                        returnedItems.findIndex((ri) => ri._id === item._id),
                        1
                      )
                    }
                    disabled={
                      returnItem.returnQuantity >= returnItem.initialQuantity
                    }
                  >
                    +
                  </Button>
                </div>
              )}

              {isReturning && (
                <div className="returnPriceContainer">
                  <span>Amount</span>
                  <p>{currencyFormatter(item.price.sellingPrice).replace(/\.00$/, "")}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


